/**
 * Lógica de cálculo de avalúos referenciales para El Salvador
 */

export const constructionClasses = {
    'BAJA': { vrn: 450.00, vut: 62.5, desc: 'Vivienda popular o de interés social.' },
    'MEDIA': { vrn: 650.00, vut: 67.5, desc: 'Vivienda intermedia con diseño profesional.' },
    'ALTA': { vrn: 950.00, vut: 72.5, desc: 'Gran residencia o mansión.' }
};

export const conditionFactors = {
    'Nuevo': { fe: 1.0000, desc: 'Sin habitar.' },
    'Bueno': { fe: 0.9968, desc: 'Mantenimiento adecuado.' },
    'Medio': { fe: 0.9748, desc: 'Mantenimiento menor requerido.' },
    'Regular': { fe: 0.9191, desc: 'Mantenimiento de mayor importancia.' },
    'Reparaciones Sencillas': { fe: 0.8190, desc: 'Necesita reparaciones importantes.' },
    'Reparaciones Medias': { fe: 0.6680, desc: 'Sustituciones completas.' },
    'Reparaciones Importantes': { fe: 0.4740, desc: 'Daños estructurales.' },
    'Daños Graves': { fe: 0.2480, desc: 'Estado ruinoso.' },
    'En desecho': { fe: 0.1350, desc: 'Inversiones no justificadas.' },
    'Inservibles': { fe: 0.0000, desc: 'Peligro de colapso.' }
};

/**
 * Calcula la depreciación basada en la edad y vida útil
 * @param {number} age - Antigüedad en años
 * @param {number} vut - Vida útil total
 * @returns {number} Factor de depreciación (1 = nuevo, <1 = depreciado)
 */
export const calculateDepreciation = (age, vut) => {
    if (!vut || vut <= 0) return 1;
    // Método lineal simple para la estimación referencial
    const factor = Math.max(0.2, 1 - (age / vut)); // 0.2 es el valor residual mínimo (20%)
    return factor;
};

/**
 * Función principal de cálculo de avalúo
 */
export const calculateAppraisalInternal = (formData) => {
    const landArea = Math.max(0, parseFloat(formData.landArea) || 0);
    const landPrice = Math.max(0, parseFloat(formData.landUnitPrice) || 0);
    const constArea = Math.max(0, parseFloat(formData.constructionArea) || 0);
    const age = Math.max(0, parseFloat(formData.age) || 0);

    const selectedClass = constructionClasses[formData.constructionClass] || constructionClasses['MEDIA'];
    const selectedCondition = conditionFactors[formData.condition] || conditionFactors['Bueno'];

    // 1. Valor del Terreno
    const vt = landArea * landPrice;

    // 2. Valor de la Construcción
    const vrn = selectedClass.vrn;
    const fe = selectedCondition.fe;
    const vut = selectedClass.vut;

    // Aplicamos depreciación combinada: Factor de Estado * Depreciación por Edad
    const ageDepreciation = calculateDepreciation(age, vut);

    // El VC es Area * VRN * (combinación de estado y edad)
    // Para no ser doblemente castigado, tomamos un promedio ponderado o el menor?
    // Usualmente es VRN * (Fe * FactorEdad)
    const combinedFactor = fe * ageDepreciation;
    const vc = constArea * vrn * combinedFactor;

    const mid = vt + vc;

    // 3. Confianza
    let confidencePoints = 0;
    if (landArea > 0 && landPrice > 0) confidencePoints += 30;
    if (constArea > 0) confidencePoints += 30;
    if (formData.address && formData.address.length > 5) confidencePoints += 20;
    if (age > 0) confidencePoints += 10;
    if (formData.condition !== 'Bueno') confidencePoints += 10; // Si el usuario se tomó el tiempo de cambiar el estado

    let confidence = 'Baja';
    let margin = 0.15; // 15% error
    if (confidencePoints >= 80) {
        confidence = 'Alta';
        margin = 0.05;
    } else if (confidencePoints >= 50) {
        confidence = 'Media';
        margin = 0.10;
    }

    const low = mid * (1 - margin);
    const high = mid * (1 + margin);

    const explanations = [
        `Valor de construcción basado en clase ${formData.constructionClass} ($${vrn}/m²).`,
        `Se aplicó factor de conservación "${formData.condition}" (${(fe * 100).toFixed(1)}%).`,
        `Depreciación por antigüedad de ${age} años (${(ageDepreciation * 100).toFixed(1)}% remanente).`,
        `Confianza ${confidence} basada en ${confidencePoints}% de datos completados.`
    ];

    return {
        vt,
        vc,
        mid,
        low,
        high,
        confidence,
        confidencePoints,
        explanations,
        vrn,
        fe,
        ageDepreciation,
        combinedFactor
    };
};
